import { useRouter } from '@tanstack/react-router';
import { getPathWithoutLocale } from 'intlayer';
import { useLocale } from 'react-intlayer';

import {
  buildLocaleParams,
  ensureLocalePrefixedPath,
  resolveAppLocale,
} from '~/utils/locale-path';
import { persistLocalePreference } from '~/utils/i18n';

/**
 * Custom hook that integrates Intlayer locale state with TanStack Router navigation.
 *
 * This hook uses Intlayer's `onLocaleChange` callback to automatically navigate
 * to the new locale URL when `setLocale()` is called.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { locale, setLocale, availableLocales } = useLocaleNavigation()
 *
 *   return (
 *     <button onClick={() => setLocale('uk')}>
 *       Switch to Ukrainian
 *     </button>
 *   )
 * }
 * ```
 *
 * @returns Object containing:
 * - `locale` - Current active locale
 * - `availableLocales` - Array of all available locales
 * - `setLocale` - Function to change locale (triggers navigation automatically)
 * - `pathWithoutLocale` - Current pathname with locale prefix removed
 */
export function useLocaleNavigation() {
  const router = useRouter();
  const { pathname } = router.state.location;
  const pathWithoutLocale = getPathWithoutLocale(pathname);

  // Use Intlayer's onLocaleChange callback to handle navigation
  type RouterNavigateOptions = Parameters<typeof router.navigate>[0];

  const { availableLocales, locale, setLocale: applyLocale } = useLocale({
    onLocaleChange: (newLocale) => {
      // When setLocale is called, navigate to the new locale URL
      const nextLocale = resolveAppLocale(newLocale);
      const newPath = ensureLocalePrefixedPath(pathWithoutLocale, nextLocale);
      const params = buildLocaleParams(nextLocale);
      const navigateOptions: RouterNavigateOptions = {
        to: newPath as RouterNavigateOptions['to'],
        params: params as RouterNavigateOptions['params'],
        replace: false,
      };
      router.navigate(navigateOptions);
    },
  });

  const setLocale = (newLocale: string) => {
    const nextLocale = resolveAppLocale(newLocale);
    persistLocalePreference(nextLocale);
    applyLocale(nextLocale);
  };

  return {
    locale,
    availableLocales,
    setLocale,
    pathWithoutLocale,
  };
}
